import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';

import SalesReportPager from './SalesReportPager';
import {
    ORDER_SORT_OPTIONS,
    activeOrderSort,
    companyLabel,
    formatGuayaquilStamp,
    formatMoney,
    orderRecognitionDate,
    orderStatusLabel,
    paymentMethodLabel,
    shortId,
} from '../../../utils/salesReportUtils';

const statusClass = (status) => {
    if (status === 'CANCELLED' || status === 'REFUNDED' || status === 'INVOICE_FAILED') return 'is-danger';
    if (status === 'INVOICED' || status === 'PAYMENT_CONFIRMED') return 'is-ok';
    return '';
};

const SortHeader = ({ label, field, activeField, sortOrder, onSort }) => {
    const active = activeField === field;
    return (
        <th aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" className="sr-sort-btn" onClick={() => onSort(field)}>
                {label}
                {active ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
            </button>
        </th>
    );
};

const SalesReportOrdersTable = ({
    orders = [],
    total = 0,
    page = 1,
    pages = 0,
    pageSize = 20,
    sortBy,
    sortOrder = 'desc',
    basis = 'paid',
    isUpdating = false,
    onPage,
    onPageSize,
    onSort,
}) => {
    const activeField = sortBy || activeOrderSort({ sortBy, basis });
    const recognitionField = activeOrderSort({ basis, sortBy: '' });
    const dateSortField = ['paidAt', 'createdAt', 'deliveredAt'].includes(activeField)
        ? activeField
        : recognitionField;

    return (
        <section className={`sr-table-card${isUpdating ? ' sr-updating' : ''}`} aria-busy={isUpdating}>
            <div className="sr-section-head">
                <div>
                    <h2>Pedidos</h2>
                    <p className="sr-hint mb-0">
                        El neto descuenta la devolución del total del pedido. Si filtras un producto, el total sigue incluyendo las otras líneas.
                    </p>
                </div>
                <div className="sr-table-toolbar">
                    <Form.Group className="sr-field mb-0">
                        <Form.Label column={false}>Ordenar por</Form.Label>
                        <Form.Select aria-label="Ordenar pedidos" value={activeField} onChange={(event) => onSort(event.target.value)}>
                            {ORDER_SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>

            {!orders.length ? (
                <p className="sr-empty mb-0">Ningún pedido coincide con estos filtros.</p>
            ) : (
                <div className="sr-table-wrap">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <SortHeader
                                    label="Fecha"
                                    field={dateSortField}
                                    activeField={activeField}
                                    sortOrder={sortOrder}
                                    onSort={onSort}
                                />
                                <th>Pedido</th>
                                <th>Cliente</th>
                                <th>Empresa</th>
                                <SortHeader label="Estado" field="orderStatus" activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Pago" field="paymentMethod" activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Total" field="totalPrice" activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <th className="sr-num">Neto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                const customer = order.customer || {};
                                const documentId = customer.taxDocId || customer.docId;
                                return (
                                    <tr key={order._id}>
                                        <td data-label="Fecha">{formatGuayaquilStamp(orderRecognitionDate(order, basis), true) || '—'}</td>
                                        <td data-label="Pedido">
                                            <span>
                                                <Link to={`/order/${order._id}`}>{shortId(order._id)}</Link>
                                                {order.invoice?.documentNumber && (
                                                    <span className="sr-subline">Factura {order.invoice.documentNumber}</span>
                                                )}
                                                {order.suborderId && <span className="sr-subline">Suborden {order.suborderId}</span>}
                                            </span>
                                        </td>
                                        <td data-label="Cliente">
                                            <span>
                                                {customer.name || 'Sin nombre'}
                                                {!customer.id && <span className="sr-status ms-1">Invitado</span>}
                                                {documentId && <span className="sr-subline">{documentId}</span>}
                                            </span>
                                        </td>
                                        <td data-label="Empresa">{companyLabel(order.company)}</td>
                                        <td data-label="Estado">
                                            <span className={`sr-status ${statusClass(order.orderStatus)}`}>
                                                {orderStatusLabel(order.orderStatus)}
                                            </span>
                                        </td>
                                        <td data-label="Pago">{paymentMethodLabel(order.paymentMethod)}</td>
                                        <td data-label="Total" className="sr-num">
                                            <span>
                                                {formatMoney(order.totalPrice)}
                                                {order.returnTotal > 0 && (
                                                    <span className="sr-subline">Dev. {formatMoney(order.returnTotal)}</span>
                                                )}
                                            </span>
                                        </td>
                                        <td data-label="Neto" className="sr-num">
                                            {formatMoney(order.netTotal)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <SalesReportPager
                page={page}
                pages={pages}
                total={total}
                pageSize={pageSize}
                onPage={onPage}
                onPageSize={onPageSize}
            />
        </section>
    );
};

export default SalesReportOrdersTable;
